import type {
  CompositionV3Filters,
  PostingsV3Filters,
  SentimentV3Filters,
  TransitionsV3Filters,
} from '@revelio/data-access';

import { type Filter, FilterType } from '../types/filters';
import { getNumericIdFromNestedId } from './get-numeric-id-from-nested-id';

/**
 * The geography keys every v3 filter input declares — verified against the dev
 * schema 2026-08-17. One list rather than four copies, so a level added to the
 * schema is a single edit here.
 *
 * A VALUE, with the type derived from it, because callers need both: the type
 * to `Pick` a filter slice, and the list itself to ask "is this level id a
 * geography one" without restating the six. `satisfies` pins every entry to a
 * real filter key, so a level the schema drops fails here rather than silently
 * matching nothing.
 */
export const GEOGRAPHY_FILTER_KEYS = [
  'region',
  'country',
  'msa',
  'state',
  'city',
  'county',
] as const satisfies readonly (keyof CompositionV3Filters)[];

type GeographyFilterKeys = (typeof GEOGRAPHY_FILTER_KEYS)[number];

type GeographyFilters =
  | Pick<CompositionV3Filters, GeographyFilterKeys>
  | Pick<TransitionsV3Filters, GeographyFilterKeys>
  | Pick<PostingsV3Filters, GeographyFilterKeys>
  | Pick<SentimentV3Filters, GeographyFilterKeys>;

/**
 * Builds the geography slice of a v3 request.
 *
 * Every v3 input this builds for declares the same geography keys, so there is
 * no per-caller capability to branch on. Pay and Screener are narrower, but
 * they build their own filters and never reach here.
 */
export const getV3GeographyFilters = (filter: Filter): GeographyFilters => {
  if (filter.type !== FilterType.TREE) return {};

  return filter.selections.reduce<GeographyFilters>((acc, selection) => {
    const selectionId = getNumericIdFromNestedId(selection.id);

    if (selectionId === null) return acc;

    if (selection.levelId === 'region') {
      return { ...acc, region: [...(acc.region ?? []), selectionId] };
    }
    if (selection.levelId === 'country') {
      return { ...acc, country: [...(acc.country ?? []), selectionId] };
    }
    if (selection.levelId === 'msa') {
      return { ...acc, msa: [...(acc.msa ?? []), selectionId] };
    }
    if (selection.levelId === 'state') {
      return { ...acc, state: [...(acc.state ?? []), selectionId] };
    }
    if (selection.levelId === 'city') {
      return { ...acc, city: [...(acc.city ?? []), selectionId] };
    }
    if (selection.levelId === 'county') {
      const withCounty = acc as Pick<CompositionV3Filters, 'county'>;
      return { ...acc, county: [...(withCounty.county ?? []), selectionId] };
    }

    return acc;
  }, {});
};
